import React, { useState } from 'react';

function ListingForm({ initialData = {}, onSubmit, submitButtonText = "Submit", loading = false }) {
  const [formData, setFormData] = useState({
    title: initialData.title || '',
    description: initialData.description || '',
    quantity: initialData.quantity || '',
    unit: initialData.unit || 'kg',
    foodType: initialData.foodType || 'produce',
    expiryDate: initialData.expiryDate ? new Date(initialData.expiryDate).toISOString().split('T')[0] : '',
    pickupInstructions: initialData.pickupInstructions || '',
    allergens: initialData.allergens || '',
    storageRequirements: initialData.storageRequirements || 'room_temperature',
    ...initialData
  });
  
  const [errors, setErrors] = useState({});

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Form validation
  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.quantity) newErrors.quantity = 'Quantity is required';
    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiryDate = new Date(formData.expiryDate);
      if (expiryDate < today) {
        newErrors.expiryDate = 'Expiry date cannot be in the past';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    } else {
      // Scroll to first error
      const firstErrorField = document.querySelector('[aria-invalid="true"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
          Title *
        </label>
        <input
          type="text"
          name="title"
          id="title"
          value={formData.title}
          onChange={handleChange}
          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
            errors.title ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
          }`}
          placeholder="Fresh Vegetables"
          aria-invalid={errors.title ? 'true' : 'false'}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title}</p>
        )}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description *
        </label>
        <textarea
          name="description"
          id="description"
          value={formData.description}
          onChange={handleChange}
          rows="3"
          className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
            errors.description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
          }`}
          placeholder="Describe the food items, their condition, and why you're donating them"
          aria-invalid={errors.description ? 'true' : 'false'}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        <div className="sm:col-span-2">
          <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
            Quantity *
          </label>
          <div className="mt-1 flex rounded-md shadow-sm">
            <input
              type="number"
              name="quantity"
              id="quantity"
              value={formData.quantity}
              onChange={handleChange}
              min="0.1"
              step="0.1"
              className={`block w-full rounded-none rounded-l-md shadow-sm sm:text-sm ${
                errors.quantity ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
              }`}
              placeholder="5"
              aria-invalid={errors.quantity ? 'true' : 'false'}
            />
            <select
              name="unit"
              value={formData.unit}
              onChange={handleChange}
              className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm"
            >
              <option value="kg">kg</option>
              <option value="g">g</option>
              <option value="lb">lb</option>
              <option value="items">items</option>
              <option value="liters">liters</option>
              <option value="servings">servings</option>
              <option value="boxes">boxes</option>
            </select>
          </div>
          {errors.quantity && (
            <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="foodType" className="block text-sm font-medium text-gray-700">
            Food Type
          </label>
          <select
            id="foodType"
            name="foodType"
            value={formData.foodType}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="produce">Fresh Produce</option>
            <option value="bakery">Bakery Items</option>
            <option value="dairy">Dairy</option>
            <option value="meat">Meat/Protein</option>
            <option value="grains">Grains/Cereals</option>
            <option value="canned">Canned/Preserved</option>
            <option value="prepared">Prepared Meals</option>
            <option value="beverages">Beverages</option>
            <option value="snacks">Snacks</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700">
            Expiry Date *
          </label>
          <input
            type="date"
            name="expiryDate"
            id="expiryDate"
            value={formData.expiryDate}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
              errors.expiryDate ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
            min={new Date().toISOString().split('T')[0]}
            aria-invalid={errors.expiryDate ? 'true' : 'false'}
          />
          {errors.expiryDate && (
            <p className="mt-1 text-sm text-red-600">{errors.expiryDate}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="allergens" className="block text-sm font-medium text-gray-700">
          Allergens (optional)
        </label>
        <input
          type="text"
          name="allergens"
          id="allergens"
          value={formData.allergens}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          placeholder="e.g., nuts, dairy, gluten"
        />
        <p className="mt-1 text-xs text-gray-500">
          List any allergens present in the food
        </p>
      </div>

      <div>
        <label htmlFor="storageRequirements" className="block text-sm font-medium text-gray-700">
          Storage Requirements
        </label>
        <select
          id="storageRequirements"
          name="storageRequirements"
          value={formData.storageRequirements}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        >
          <option value="refrigerated">Refrigerated</option>
          <option value="frozen">Frozen</option>
          <option value="room_temperature">Room Temperature</option>
        </select>
      </div>

      <div>
        <label htmlFor="pickupInstructions" className="block text-sm font-medium text-gray-700">
          Pickup Instructions (optional)
        </label>
        <textarea
          name="pickupInstructions"
          id="pickupInstructions"
          value={formData.pickupInstructions}
          onChange={handleChange}
          rows="2"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          placeholder="Any special instructions for pickup"
        />
      </div>

      <div className="flex justify-end pt-5">
        <button
          type="submit"
          disabled={loading}
          className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
            loading ? 'opacity-70 cursor-not-allowed' : ''
          }`}
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            submitButtonText
          )}
        </button>
      </div>
    </form>
  );
}

export default ListingForm;
